import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  BadgeCheck,
  CalendarDays,
  Flame,
  Globe,
  Link2,
  MapPin,
  MousePointerClick,
  Tag,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { SocialLinks } from "@/components/socials";
import { DiscoverEntry } from "@/components/board";
import { ListingEndorsements } from "@/components/ambassador-endorsements";
import { ListingCreatorCard } from "@/components/listing-creator-card";
import { ShareButtons } from "@/components/share-buttons";
import { ListingPlacedDialog } from "@/components/listing-placed-dialog";
import { useMyReferralCode } from "@/hooks/useReferral";
import { useAuth } from "@/hooks/useAuth";

import { HistoryAchievements } from "@/components/history/history-achievements";
import {
  listingBySlugQuery,
  listingBoardsQuery,
  listingBoostHistoryQuery,
  listingPositionsQuery,
  listingGeneralRankQuery,
  discoverListingsQuery,
  trackListingClick,
} from "@/lib/queries";
import { buildPageMetadata, SITE_URL } from "@/lib/seo";
import { canonicalDomain, formatHeat, formatUsd, labelForType, outboundUrl } from "@/lib/behot";
import { analytics } from "@/lib/analytics";
import { proxiedImage } from "@/lib/remote-image";

/** Mirrored listing images are stored as relative paths; crawlers need absolute https URLs. */
function absoluteImage(url?: string | null) {
  if (!url) return null;
  if (url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `${SITE_URL}${url}`;
  return null;
}

type ListingSearch = { placed?: "1" | undefined; board?: string | undefined; ref?: string | undefined };

export const Route = createFileRoute("/p/$slug")({
  validateSearch: (search: Record<string, unknown>): ListingSearch => ({
    ...(search["placed"] === "1" || search["placed"] === 1 ? { placed: "1" as const } : {}),
    ...(typeof search["board"] === "string" ? { board: search["board"] } : {}),
    ...(typeof search["ref"] === "string" ? { ref: search["ref"] } : {}),
  }),

  loader: ({ params, context }) =>
    context.queryClient.ensureQueryData(listingBySlugQuery(params.slug)),
  head: ({ params, loaderData }) => {

    const listing = loaderData ?? null;
    if (!listing) {
      return buildPageMetadata({
        path: `/p/${params.slug}`,
        title: "Listing not found | BeHot.Now",
        description: "This listing is not available on BeHot.Now.",
        noindex: true,
      });
    }
    const label = listing.listing_type_label || labelForType(listing.listing_type);
    const location = [listing.city, listing.country_code].filter(Boolean).join(", ");
    const summary =
      listing.headline ||
      listing.short_description ||
      `${listing.name}${location ? ` in ${location}` : ""}`;
    return buildPageMetadata({
      path: `/p/${listing.slug}`,
      title: `${listing.name} — ${label} Profile & Live Rank | BeHot.Now`,
      description:
        `Discover ${listing.name}, ${summary}. See its BeHot.Now category, current live ranking, website and verified public links.`.slice(
          0,
          300,
        ),
      ogTitle: `${listing.name} — ${label} on BeHot.Now`,
      ogDescription: summary,
      image: absoluteImage(listing.cover_url || listing.logo_url),
      imageAlt: `${listing.name} on BeHot.Now`,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "What's hot", path: "/hot" },
        { name: listing.name, path: `/p/${listing.slug}` },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: listing.name,
        url: `${SITE_URL}/p/${listing.slug}`,
        ...(absoluteImage(listing.logo_url) ? { logo: absoluteImage(listing.logo_url) } : {}),
        ...(absoluteImage(listing.cover_url || listing.logo_url)
          ? { image: absoluteImage(listing.cover_url || listing.logo_url) }
          : {}),
        description: summary,
        ...(listing.website_url ? { sameAs: [listing.website_url] } : {}),
      },
    });
  },
  component: ListingPage,
});

function Stat({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: typeof Flame;
}) {
  return (
    <div className="card-surface p-4">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        <Icon className="size-3.5" /> {label}
      </p>
      <p className="mt-1 font-display text-xl font-bold">{value}</p>
    </div>
  );
}

function ListingPage() {
  const { t } = useT();
  const { slug } = Route.useParams();
  const { placed, board } = Route.useSearch();
  const refCode = useMyReferralCode();
  const { user } = useAuth();
  const { data: listing, isLoading } = useQuery(listingBySlugQuery(slug));

  const { data: boards = [] } = useQuery(listingBoardsQuery(listing?.id));
  const { data: positions = [] } = useQuery(listingPositionsQuery(listing?.id));
  const { data: generalRank } = useQuery(listingGeneralRankQuery(listing?.id));
  const { data: history = [] } = useQuery(listingBoostHistoryQuery(listing?.id));
  const primaryBoard = boards[0];

  useEffect(() => {
    if (listing) {
      analytics.viewListing({
        slug: listing.slug,
        name: listing.name,
        category: boards[0]?.name,
      });
    }
  }, [listing, boards]);
  const { data: related = [] } = useQuery(
    discoverListingsQuery(positions[0]?.category.id ? [positions[0].category.id] : []),
  );

  if (isLoading) {
    return <div className="mx-auto max-w-3xl px-4 py-20 text-muted-foreground">{t("profiles.loading")}</div>;
  }

  if (!listing) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold">{t("profiles.listingNotFound")}</h1>
        <Button asChild variant="hot" className="mt-4">
          <Link to="/hot">{t("profiles.browseBoards")}</Link>
        </Button>
      </div>
    );
  }

  const domain = canonicalDomain(listing.website_url);
  const totalHeat = positions.reduce((sum, p) => sum + p.heat_cents, 0);
  const bestRank = positions.filter((p) => p.rank).sort((a, b) => a.rank! - b.rank!)[0];
  const relatedOther = related.filter((l) => l.id !== listing.id).slice(0, 4);
  const placedPosition = board ? positions.find((p) => p.category.slug === board) : bestRank;
  const shareMessage = bestRank?.rank
    ? t("profiles.placedShareMessage", {
        name: listing.name,
        rank: `#${String(bestRank.rank).padStart(2, "0")}`,
        board: bestRank.category.name,
        heat: formatHeat(bestRank.heat_cents),
      })
    : `${listing.name} on BeHot.Now`;


  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: listing.name,
    description: listing.short_description ?? listing.headline ?? undefined,
    url: listing.website_url ?? undefined,
    logo: listing.logo_url ?? undefined,
  };

  return (
    <article className="pb-16">
      {placed && (
        <ListingPlacedDialog
          isOwner={user?.id != null && user.id === listing.owner_user_id}
          listingName={listing.name}
          listingSlug={listing.slug}
          shortCode={listing.short_code ?? null}
          boardName={placedPosition?.category.name ?? primaryBoard?.name ?? t("profiles.generalCategory")}
          boardSlug={placedPosition?.category.slug ?? primaryBoard?.slug ?? null}
          rank={placedPosition?.rank ?? bestRank?.rank ?? null}
          heatCents={placedPosition?.heat_cents ?? totalHeat}
          generalRank={generalRank?.rank ?? null}
          generalHeatCents={generalRank?.heat_cents ?? totalHeat}
        />
      )}
      <div className="mx-auto max-w-5xl px-4 pt-6 sm:pt-10">
        {/* Header: logo + name + badges + actions */}
        <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
          {listing.logo_url ? (
            <img
              src={proxiedImage(listing.logo_url)}
              alt={`${listing.name} logo`}
              className="size-16 shrink-0 rounded-2xl border-4 border-background bg-card object-cover shadow-sm sm:size-24"
            />
          ) : (
            <div className="flex size-16 shrink-0 items-center justify-center rounded-2xl border-4 border-background bg-accent text-2xl font-bold text-accent-foreground shadow-sm sm:size-24 sm:text-4xl">
              {listing.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary">{labelForType(listing.listing_type)}</Badge>
              {listing.verification_status === "verified" && (
                <Badge className="gap-1">
                  <BadgeCheck className="size-3" /> {t("profiles.verified")}
                </Badge>
              )}
              {listing.claim_status === "unclaimed" && (
                <Badge variant="outline">{t("profiles.unclaimed")}</Badge>
              )}
            </div>
            <h1 className="mt-2 break-words font-display text-2xl font-bold text-foreground sm:text-4xl lg:text-5xl">
              {listing.name}
            </h1>
            {listing.headline && (
              <p className="mt-1 max-w-2xl break-words text-sm text-muted-foreground sm:text-lg">{listing.headline}</p>
            )}

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
              <Button asChild variant="hot" size="lg" className="w-full sm:w-auto">
                <Link
                  to={primaryBoard ? "/boost" : "/get-hot"}
                  search={{ listing: listing.slug, category: primaryBoard?.slug }}
                >
                  {t("profiles.boostThisListing")} <Flame className="size-4" />
                </Link>
              </Button>
              {listing.website_url && (
                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                  <a
                    href={outboundUrl(listing.website_url, {
                      slug: listing.slug,
                      medium: "profile",
                      content: primaryBoard?.slug ?? "listing-page",
                    })}
                    target="_blank"
                    rel="noopener"
                    onClick={() => void trackListingClick(listing.slug)}
                    className="min-w-0"
                  >
                    <Globe className="size-4 shrink-0" />
                    <span className="truncate">{domain ?? t("profiles.website")}</span>
                  </a>
                </Button>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <SocialLinks socials={listing.socials} />
              </div>
            </div>

            <ShareButtons
              className="mt-4"
              path={listing.short_code ? `/${listing.short_code}` : `/p/${listing.slug}`}
              refCode={refCode}
              title={shareMessage}
            />
          </div>
        </header>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat
            label={t("profiles.activeHeat")}
            value={`${formatHeat(totalHeat)} Heat`}
            icon={Flame}
          />
          <Stat
            label={t("profiles.bestPosition")}
            value={bestRank?.rank ? `#${String(bestRank.rank).padStart(2, "0")}` : t("profiles.na")}
            icon={Users}
          />
          <Stat label={t("profiles.boards")} value={String(positions.length)} icon={Tag} />
          <Stat
            label={t("profiles.clicks")}
            value={new Intl.NumberFormat("en-US").format(listing.click_count)}
            icon={MousePointerClick}
          />
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0 space-y-8">
            <ListingEndorsements listingId={listing.id} />
            <HistoryAchievements listingId={listing.id} />
            <ListingCreatorCard ownerUserId={listing.owner_user_id ?? undefined} listingId={listing.id} />
            {listing.cover_url && (
              <section className="card-surface overflow-hidden p-2 sm:p-3">
                <img
                  src={proxiedImage(listing.cover_url)}
                  alt={`${listing.name} cover`}
                  className="w-full rounded-xl object-contain"
                />
              </section>
            )}


            {(listing.long_description || listing.short_description) && (
              <section className="card-surface p-6">
                <h2 className="font-display text-lg font-bold">{t("profiles.about")}</h2>
                <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
                  {listing.long_description || listing.short_description}
                </p>

                <div className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
                  {(listing.city || listing.country_code) && (
                    <p className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="size-4" />
                      {[listing.city, listing.country_code].filter(Boolean).join(", ")}
                    </p>
                  )}
                  {listing.founded_year && (
                    <p className="flex items-center gap-2 text-muted-foreground">
                      <CalendarDays className="size-4" /> {t("profiles.founded")} {listing.founded_year}
                    </p>
                  )}
                  {listing.team_size && (
                    <p className="flex items-center gap-2 text-muted-foreground">
                      <Users className="size-4" /> {listing.team_size}
                    </p>
                  )}
                  {listing.pricing_note && (
                    <p className="flex items-center gap-2 text-muted-foreground">
                      <Tag className="size-4" /> {listing.pricing_note}
                    </p>
                  )}
                </div>

                {listing.tags && listing.tags.length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    {listing.tags.map((t) => (
                      <Badge key={t} variant="outline">
                        {t}
                      </Badge>
                    ))}
                  </div>
                )}
              </section>
            )}

            <section className="card-surface p-6">
              <h2 className="font-display text-lg font-bold">{t("profiles.boostHistory")}</h2>
              {history.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">
                  {t("profiles.noActiveHeatYet")}
                </p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {history.map((b) => (
                    <li key={b.id} className="flex items-center gap-3 text-sm">
                      <Flame className="size-4 shrink-0 text-primary" />
                      <span className="font-semibold">{formatUsd(b.value_cents)}</span>
                      <Badge variant="outline" className="text-xs font-normal">
                        {b.category?.name ?? t("profiles.generalCategory")}
                      </Badge>
                      <span className="ml-auto text-xs text-muted-foreground">
                        {t("profiles.cools")} {new Date(b.expires_at).toLocaleDateString()}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {relatedOther.length > 0 && (
              <section>
                <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
                  {t("profiles.alsoOnThisBoard")}
                </h2>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {relatedOther.map((l) => (
                    <DiscoverEntry key={l.id} listing={l} />
                  ))}
                </div>
              </section>
            )}
          </div>

          <aside className="space-y-4">
            <div className="card-surface p-5">
              <h2 className="font-display text-lg font-bold">{t("profiles.positions")}</h2>
              <Separator className="my-3" />
              {positions.length === 0 && (
                <p className="text-sm text-muted-foreground">{t("profiles.notOnBoardYet")}</p>
              )}
              <ul className="space-y-3">
                {positions.map((p) => (
                  <li key={p.category.id} className="flex items-center gap-3">
                    <span className="font-display text-lg font-bold text-primary">
                      {p.rank ? `#${String(p.rank).padStart(2, "0")}` : t("profiles.na")}
                    </span>
                    <div className="min-w-0 flex-1">
                      <Link
                        to="/c/$slug"
                        params={{ slug: p.category.slug }}
                        className="block truncate text-sm font-semibold hover:text-primary"
                      >
                        {p.category.name}
                      </Link>
                      <p className="text-xs text-muted-foreground">
                        {formatHeat(p.heat_cents)} Heat · {t("profiles.competing", { n: p.competitors })}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
              <Button asChild variant="hot" className="mt-4 w-full">
                <Link
                  to={primaryBoard ? "/boost" : "/get-hot"}
                  search={{ listing: listing.slug, category: primaryBoard?.slug }}
                >
                  {t("profiles.addHeat")}
                </Link>
              </Button>
            </div>

            <p className="px-1 text-xs text-muted-foreground">
              {t("profiles.paidPlacementNote")}
            </p>
          </aside>
        </div>
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    </article>
  );
}
